import { isWebView } from "@/constants/webView";
import sendMessageToWebViewEvent from "@/services/webViewEvents/sendMessageToWebViewEvent";
import { getCookie } from "@/utils/cookie";

const APP_WEB_VIEW_VERSION = 3;
const MANAGE_STORAGE_BANNER_MIN_WEB_VIEW_VERSION = 6;

const EVENT_TYPE = {
  ANALYTICS: "analytics_event",
  DOWNLOAD: "download",
  REDIRECT: "redirect",
} as const;

type EventType = (typeof EVENT_TYPE)[keyof typeof EVENT_TYPE];

export const WEB_VIEW_MESSAGES = {
  APP_SUBSCRIPTION_LITE: "app_subscription_lite",
  APP_SUBSCRIPTION_PREMIUM: "app_subscription_premium",
  APP_CUSTOMIZE_QR: "app_customize_qr",
  APP_TYPE_QR: "app_type_qr",
  APP_DOWNLOAD_QR: "app_download_qr",
  MY_QR_CODES: "my_qr_codes",
  PRICING: "pricing",
  DELETE_USER: "delete_user",
  MANAGE_STORAGE: "manage_storage",
} as const;

interface StructuredEvent {
  type: EventType;
  payload: string;
}

function getWebViewVersion(): number | null {
  const version = getCookie("app_web_view_version");
  return version ? parseInt(version, 10) : null;
}

function isStructuredEvents(): boolean {
  const version = getWebViewVersion();
  return version !== null && version >= APP_WEB_VIEW_VERSION;
}

function isManageStorageBannerSupported(): boolean {
  const version = getWebViewVersion();
  return (
    version !== null && version >= MANAGE_STORAGE_BANNER_MIN_WEB_VIEW_VERSION
  );
}

function postMessage(data: string | StructuredEvent): void {
  if (!isWebView) return;

  if (typeof data === "object") {
    sendMessageToWebViewEvent(JSON.stringify(data));
  } else {
    sendMessageToWebViewEvent(data);
  }
}

function sendEvent(type: EventType, payload: string): void {
  if (!isWebView) return;
  postMessage({ type, payload });
}

const webViewEventService = {
  isStructuredEvents,
  isManageStorageBannerSupported,
  getWebViewVersion,

  subscriptionLite(): void {
    sendEvent(EVENT_TYPE.ANALYTICS, WEB_VIEW_MESSAGES.APP_SUBSCRIPTION_LITE);
  },

  subscriptionPremium(): void {
    sendEvent(EVENT_TYPE.ANALYTICS, WEB_VIEW_MESSAGES.APP_SUBSCRIPTION_PREMIUM);
  },

  customizeQr(): void {
    sendEvent(EVENT_TYPE.ANALYTICS, WEB_VIEW_MESSAGES.APP_CUSTOMIZE_QR);
  },

  selectQrType(): void {
    sendEvent(EVENT_TYPE.ANALYTICS, WEB_VIEW_MESSAGES.APP_TYPE_QR);
  },

  downloadQr(): void {
    sendEvent(EVENT_TYPE.ANALYTICS, WEB_VIEW_MESSAGES.APP_DOWNLOAD_QR);
  },

  linkToMyQrCodes(): void {
    sendEvent(EVENT_TYPE.REDIRECT, WEB_VIEW_MESSAGES.MY_QR_CODES);
  },

  linkToPricing(): void {
    sendEvent(EVENT_TYPE.REDIRECT, WEB_VIEW_MESSAGES.PRICING);
  },

  manageStorage(): void {
    sendEvent(EVENT_TYPE.REDIRECT, WEB_VIEW_MESSAGES.MANAGE_STORAGE);
  },

  downloadImage(payload: string): void {
    sendEvent(EVENT_TYPE.DOWNLOAD, payload);
  },

  redirect(payload: string): void {
    sendEvent(EVENT_TYPE.REDIRECT, payload);
  },

  deleteUser(): void {
    sendEvent(EVENT_TYPE.REDIRECT, WEB_VIEW_MESSAGES.DELETE_USER);
  },
};

export default webViewEventService;
